import * as React from "react";
import * as ReactDOM from "react-dom";
import { Route } from "react-router-dom";
import * as moment from "moment";
import * as globals from "../../globals";
import * as enums from "../../enumerations";
import * as utils from "../../utils/utils";
import * as utilsCalendar from "../../utils/calendar";
import * as utilsConstants from "../../utils/constants";
import * as utilsStorage from "../../utils/localstorage";
import * as utilsViewMode from "../../utils/viewmode";
import * as utilsColor from "../../utils/color";
import * as modelsGet from "../../models/get";
import * as modelsCalendar from "../../models/calendar";
import * as modelsChangeset from "../../models/changeset";
import * as apiUsers from "../../api/users";
import * as apiCalendars from "../../api/calendars";
import * as apiCalendarGroups from "../../api/calendarGroups";
import * as apiStatus from "../../api/status";
import * as apiEvents from "../../api/events";
import * as apiAppointments from "../../api/appointments";
import * as apiTimes from "../../api/times";
import * as apiSettings from "../../api/settings";
import { SideBar } from "./sidebar";
import { CalendarMonth } from "./calendarMonth";
import { CalendarDay } from "./calendarDay";
import { CalendarPlanning } from "./calendarPlanning";
import { ToolBar, ToolBarItem, ToolBarSeparator } from "../toolbar";
import { ICalendarStorage } from "../../models/storage";
import { PopupProvider, Popup, PopupTrigger, PopupAlign } from "../popup";
import { ContextMenu, MenuItem, Separator } from "../contextmenu/index";
import { Datepicker } from "../datepicker";
import { Time } from "../../types/time";
import { Loader, LoaderStyle } from "../loader";

interface IProperties
{
    /**
     * Angabe, ob der Kalender aktualisiert werden soll.
     */
    refresh: boolean,

    /**
     * Die momentane Route.
     */
    route: () => uri.URI,

    /**
     * Der selektierte Kalender oder die Kalendergruppe.
     */
    selectedObject: modelsCalendar.ISelectedObjectId,

    /**
     * Selektierte Datum.
     */
    selectedDate: moment.Moment,

    /**
     * Der selektierte Kalender (nur wenn eine Kalendergruppe ausgewählt ist).
     */
    selectedCalendar: number,

    /**
     * Die selektierte Kalenderansicht.
     */
    selectedViewMode: enums.CalendarViewMode
}

interface IState
{
    isLoading?: boolean,

    /**
     * Der momentan angemeldete Benutzer.
     */
    user?: modelsGet.IUser,

    /**
     * Enthält den momentan selektierten Kalender.
     */
    selectedObject?: modelsCalendar.ISelectedObject,

    /**
     * Enthält die Farbe des selektierten Kalender.
     */
    selectedCalendar?: modelsGet.ICalendar,

    /**
     * Enthält die kalkulierte Zeitskala.
     */
    hours?: modelsCalendar.IHour[],

    /**
     * Enthält die Wochen und Tage für das rendern der Kalenderansichten.
     */
    weeks?: modelsCalendar.IWeek[],

    /**
     * Enthält die Tage für das rendern der Kalenderansichten.
     */
    days?: modelsCalendar.IDayOfWeek[],

    /**
     * Enthält alle eingerichteten Statuseinträge.
     */
    status?: modelsGet.IStatus[],

    timeGroups?: modelsGet.ITimeGroup[],

    /**
     * Enthält alle Termine und gesperrte Zeiträume die im Kalender angezeigt werden sollen.
     */
    appointments?: modelsGet.IAppointment[],

    /**
     * Enthält alle von der Komponente benötigten Einstellungen.
     */
    settings?: {
        hiddenDaysInWorkWeek?: number[],
        eventDayViewTemplate?: string,
        showLabelsAsInitials?: boolean,
        timeGroups?: modelsGet.ITimeGroup[]
    }

    /**
     * Anzahl der sichtbaren Datepicker.
     */
    visibleDatepickerCount?: number,

    /**
     * Angabe, ob die SideBar geöffnet ist.
     */
    isSideBarOpen?: boolean,

    /**
     * Enthält den Termin auf den der Mauszeiger zeigt.
     */
    popupAppointment?: modelsGet.IAppointment,

    /**
     * Enthält die Anzahl von Links des Termins auf den der Mauszeiger zeigt.
     */
    popupAppointmentLinkCount?: number,

    /**
     * Enthält die letzte Änderung des Termins auf den der Mauszeiger zeigt.
     */
    popupAppointmentLastChange?: modelsChangeset.ILastChange,

    /**
     * Enthält die Sperre auf den der Mauszeiger zeigt.
     */
    popupBlock?: modelsGet.IAppointment,
}

export class Calendar extends React.Component<IProperties, IState>
{
    /**
     * Enthält die ID eines kopierten Termins.
     */
    private _copiedAppointmentId: number = 0;

    /**
     * Enthält die Subject Type ID eines kopierten Termins.
     */
    private _subjectTypeId: number = null;

    /**
     * Erstellt eine neue Instanz der Komponente @see Calendar.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        var storage = utilsStorage.getJSON<ICalendarStorage>(utilsConstants.STORAGE_KEYS.calendar, true);
        if (storage == null)
        {
            storage = {};
            storage.visibleDatepickerCount = 1;
            storage.isSideBarOpen = true;
        }

        this.state = {
            isLoading: true,
            settings: {},
            visibleDatepickerCount: storage.visibleDatepickerCount,
            isSideBarOpen: storage.isSideBarOpen == null ? true : storage.isSideBarOpen
        };

        this.initAsync(props);
    }

    /**
     * Wird ausgeführt, sobald die Komponente neue Eigenschaften erhält.
     * Hier werden die benötigten Daten erneut geladen.
     * @param nextProps Die nächsten/neuen Eigenschaften.
     */
    componentWillReceiveProps(nextProps: IProperties)
    {
        const oldProp = this.props;
        const newProp = nextProps;

        if (oldProp.selectedObject.type !== newProp.selectedObject.type ||
            oldProp.selectedObject.id !== newProp.selectedObject.id ||
            !oldProp.selectedDate.isSame(newProp.selectedDate) ||
            oldProp.selectedCalendar !== newProp.selectedCalendar ||
            oldProp.selectedViewMode !== newProp.selectedViewMode)
        {
            this.initAsync(nextProps);
        }
        else if (newProp.refresh)
        {
            this.getAppointments(this.state.weeks).then((data) =>
            {
                // Die Termine und gesperrten Zeiten den richtigen Wochen und Tagen zuordnen.
                this.setAppointmentsForWeeks(this.state.weeks, this.state.selectedObject, data);

                // Die Tage für die anderen Ansichten ermitteln.
                this.setAppointmentsForDays(this.state.days, this.state.selectedObject, data);

                // Die anzuzeigenden freien Blöcke generieren.
                this.getFreeBlocks(newProp, this.state.selectedObject, this.state.days, this.state.timeGroups).then((freeBlocks) =>
                {
                    this.setState({
                        isLoading: false,
                        appointments: data
                    });
                });
            });
        }
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        if (!this.state.isLoading)
        {
            this.setState({
                isLoading: true
            });
        }

        // Momentan angemeldeten Benutzer laden.
        var currentUser: modelsGet.IUser = await globals.getCurrentUserAsync();

        // Alle Kalendergruppen laden.
        var calendarGroups = await globals.getCalendarGroupsAsync();

        // Alle Kalender laden.
        var calendars = await globals.getCalendarsAsync();

        // Alle Status Einträge laden.
        var status = await globals.getStatusAsync();

        // Momentan selektierten Kalender oder Kalendergruppe ermitteln.
        var selectedObject = utilsCalendar.getSelectedObject(props.selectedObject, calendarGroups, calendars);

        // Momentan selektierten Kalender ermitteln. Ist nur gefüllt wenn eine Kalendergruppe
        // ausgewählt ist.
        var selectedCalendar: modelsGet.ICalendar = null;
        if (props.selectedCalendar != null && props.selectedCalendar > 0)
            selectedCalendar = calendars.where(c => c.id === props.selectedCalendar).first();

        // Einstellungen laden.
        var hiddenDaysInWorkWeek = await globals.getSettingAsync<number[]>(globals.SettingKey.daysHiddenInWorkweek);
        var eventDayViewTemplate = await globals.getSettingAsync<string>(globals.SettingKey.eventDVTemplate);
        var showLabelAsInitials = await globals.getSettingAsync<boolean>(globals.SettingKey.displayEventShowLabelInitials);
        var timeGroups = await globals.getSettingAsync<modelsGet.ITimeGroup[]>(globals.SettingKey.rulesCalendarsTimeGroups);

        // Stunden ermitteln.
        var hours: modelsCalendar.IHour[] = utilsCalendar.getHours();

        // Die Wochen und Tage zum rendern der Kalenderansichten generieren.
        var weeks: modelsCalendar.IWeek[] = utilsCalendar.getWeeks(props.selectedDate);

        var days: modelsCalendar.IDayOfWeek[] = utilsCalendar.getDays(props.selectedDate, props.selectedViewMode, hiddenDaysInWorkWeek);

        // Alle Termin in der selektierten Zeitspanne ermitteln.
        var appointments: modelsGet.IAppointment[] = await this.getAppointments(weeks);

        // State setzen.
        selectedObject.all((cal) =>
        {
            let selectedObject: modelsCalendar.ISelectedObject = {
                type: enums.CalendarObjectType.all,
                data: null,
                calendarsInGroup: calendars
            };

            // Die Termine und gesperrten Zeiten den richtigen Wochen und Tagen zuordnen.
            this.setAppointmentsForWeeks(weeks, selectedObject, appointments);

            // Die Tage für die anderen Ansichten ermitteln.
            this.setAppointmentsForDays(days, selectedObject, appointments);

            // Die anzuzeigenden freien Blöcke generieren.
            this.getFreeBlocks(props, selectedObject, days, timeGroups).then((freeBlocks) =>
            {
                this.setState({
                    isLoading: false,
                    user: currentUser,
                    selectedObject: selectedObject,
                    selectedCalendar: selectedCalendar,
                    hours: hours,
                    weeks: weeks,
                    days: days,
                    status: status,
                    timeGroups: timeGroups,
                    appointments: appointments,
                    settings: {
                        hiddenDaysInWorkWeek: hiddenDaysInWorkWeek,
                        eventDayViewTemplate: eventDayViewTemplate,
                        showLabelsAsInitials: showLabelAsInitials,
                        timeGroups: timeGroups
                    }
                });
            });
        });

        selectedObject.calendar((cal) =>
        {
            let selectedObject: modelsCalendar.ISelectedObject = {
                type: enums.CalendarObjectType.calendar,
                data: cal
            };

            // Die Termine und gesperrten Zeiten den richtigen Wochen und Tagen zuordnen.
            this.setAppointmentsForWeeks(weeks, selectedObject, appointments);

            // Die Tage für die anderen Ansichten ermitteln.
            this.setAppointmentsForDays(days, selectedObject, appointments);

            // Die anzuzeigenden freien Blöcke generieren.
            this.getFreeBlocks(props, selectedObject, days, timeGroups).then((freeBlocks) =>
            {
                this.setState({
                    isLoading: false,
                    user: currentUser,
                    selectedObject: selectedObject,
                    selectedCalendar: cal,
                    hours: hours,
                    weeks: weeks,
                    days: days,
                    status: status,
                    timeGroups: timeGroups,
                    appointments: appointments,
                    settings: {
                        hiddenDaysInWorkWeek: hiddenDaysInWorkWeek,
                        eventDayViewTemplate: eventDayViewTemplate,
                        showLabelsAsInitials: showLabelAsInitials,
                        timeGroups: timeGroups
                    }
                });
            });
        });

        selectedObject.calendarGroup((grp) =>
        {
            let selectedObject: modelsCalendar.ISelectedObject = {
                type: enums.CalendarObjectType.calendarGroup,
                data: grp,
                calendarsInGroup: calendars.where(c => grp.calendars.any(c2 => c2.calendar_id === c.id))
            };

            // Die Termine und gesperrten Zeiten den richtigen Wochen und Tagen zuordnen.
            this.setAppointmentsForWeeks(weeks, selectedObject, appointments);

            // Die Tage für die anderen Ansichten ermitteln.
            this.setAppointmentsForDays(days, selectedObject, appointments);

            // Die anzuzeigenden freien Blöcke generieren.
            this.getFreeBlocks(props, selectedObject, days, timeGroups).then((freeBlocks) =>
            {
                this.setState({
                    isLoading: false,
                    user: currentUser,
                    selectedObject: selectedObject,
                    selectedCalendar: selectedCalendar,
                    hours: hours,
                    weeks: weeks,
                    days: days,
                    status: status,
                    timeGroups: timeGroups,
                    appointments: appointments,
                    settings: {
                        hiddenDaysInWorkWeek: hiddenDaysInWorkWeek,
                        eventDayViewTemplate: eventDayViewTemplate,
                        showLabelsAsInitials: showLabelAsInitials,
                        timeGroups: timeGroups
                    }
                });
            });
        });
    }

    /**
     * Gibt alle Termine und gesperrte Zeiträume für den momentan selektierte Zeitraum zurück.
     * @param weeks Die kalkulierten Wochen.
     */
    private async getAppointments(weeks: modelsCalendar.IWeek[]): Promise<modelsGet.IAppointment[]>
    {
        const { selectedDate, selectedViewMode } = this.props;
                
        if (selectedViewMode === enums.CalendarViewMode.day || selectedViewMode === enums.CalendarViewMode.planning)
        {
            return apiAppointments.getAppointments(moment(selectedDate.format("L") + " " + "08:00", "DD.MM.YYYY HH:mm"));
        }
        else
        {
            let firstOfMonth = weeks.first().daysOfWeek.first().date;
            let endOfMonth = moment(weeks.last().daysOfWeek.last().date).add(1, "days");
            return apiAppointments.getAppointments(firstOfMonth, endOfMonth);
        }
    }

    /**
     * Prüft die Zugehörigkeit eines jeden Termins zu einem Tag.
     * @param weeks Die kalkulierten Wochen.
     * @param selectedObject Der momentan selektierte Kalender oder die Kalendergruppe.
     * @param appointments Alle, zum selektierten Zeitraum geladenen Termine.
     */
    private setAppointmentsForWeeks(weeks: modelsCalendar.IWeek[], selectedObject: modelsCalendar.ISelectedObject, appointments: modelsGet.IAppointment[])
    {
        if (this.props.selectedViewMode !== enums.CalendarViewMode.month)
            return;

        for (let week of weeks)
        {
            for (let day of week.daysOfWeek)
            {
                day.appointments = [];

                for (let appointment of appointments)
                {
                    let isValid = utilsCalendar.isAppointmentValid(day.date, appointment, selectedObject);
                    if (isValid)
                        day.appointments.push(appointment);
                }
            }
        }
    }

    /**
     * Prüft die Zugehörigkeit eines jeden Termins zu einem Tag.
     * @param days Die kalkulierten Tage.
     * @param selectedObject Der momentan selektierte Kalender oder die Kalendergruppe.
     * @param appointments Alle, zum selektierten Zeitraum geladenen Termine.
     */
    private setAppointmentsForDays(days: modelsCalendar.IDayOfWeek[], selectedObject: modelsCalendar.ISelectedObject, appointments: modelsGet.IAppointment[])
    {
        if (this.props.selectedViewMode === enums.CalendarViewMode.month)
            return;

        var checkDate: boolean = !(this.props.selectedViewMode === enums.CalendarViewMode.planning || this.props.selectedViewMode === enums.CalendarViewMode.day);
        for (let day of days)
        {
            day.appointments = [];

            for (let appointment of appointments)
            {
                let isValid = utilsCalendar.isAppointmentValid(day.date, appointment, selectedObject, checkDate);
                if (isValid)
                    day.appointments.push(appointment);
            }
        }
    }

    /**
     * Freie Blöcke ermitteln/generieren.
     * @param props Die Eigenschaften dieser Komponente.
     */
    private async getFreeBlocks(props: IProperties, selectedObject: modelsCalendar.ISelectedObject, days: modelsCalendar.IDayOfWeek[], timeGroups: modelsGet.ITimeGroup[])
    {
        if (props.selectedViewMode === enums.CalendarViewMode.month)
            return;

        // Alle Benutzer ermitteln.
        var users: modelsGet.IUser[] = await globals.getUsersAsync();

        // Selektierte Kalender ermitteln.
        var selectedCalendarIds: number[] = [];
        if (selectedObject.type === enums.CalendarObjectType.calendar)
        {
            selectedCalendarIds.push(selectedObject.data.id);
        }
        else if (selectedObject.type === enums.CalendarObjectType.calendarGroup)
        {
            let group: modelsGet.ICalendarGroup = selectedObject.data as modelsGet.ICalendarGroup;
            selectedCalendarIds.push(...group.calendars.select(c => c.calendar_id));
        }
        else
        {
            selectedCalendarIds.push(...selectedObject.calendarsInGroup.select(c => c.id));
        }

        // Freie Blöcke erstellen.
        var availableTime: modelsCalendar.IAvailableTime[] = [];
        var calendars: modelsGet.ICalendar[] = await apiCalendars.getCalendars(selectedCalendarIds);
        for (let calendar of calendars)
        {
            // IDs aus den Zeitgruppen ermitteln.
            let calIds: number[] = [];
            if (timeGroups != null && timeGroups.length > 0 && timeGroups.any(t => t.calendars && t.calendars.contains(calendar.id)))
            {
                for (let timeGroup of timeGroups)
                {
                    calIds.push(...timeGroup.calendars)
                }
            }

            calIds = calIds.distinct();
            calIds.remove(calendar.id);

            // Eingestellte Zeitspanne aus dem Kalender laden.
            let calSettings: modelsGet.ICalendarSettings = JSON.parse(calendar.settings);
            let rangeStart: Time = Time.parse(calSettings.time.range[0]);
            let rangeEnd: Time = Time.parse(calSettings.time.range[1]);

            let defaultInterval: number = calSettings.time.default_interval;
            let setuptime: number = calSettings.time.setuptime > 0 ? calSettings.time.setuptime : 0;
            while (rangeStart.lessThan(rangeEnd))
            {
                availableTime.push({
                    calendarId: calendar.id,
                    rangeStart: rangeStart,
                    defaultInterval: defaultInterval,
                    setuptime: setuptime,
                    calendarIds: calIds
                });

                rangeStart = Time.parse(moment().startOf("day").add(rangeStart.Hours, "hours").add(rangeStart.Minutes + defaultInterval + setuptime, "minutes").add(rangeStart.Seconds, "seconds").format("HH:mm"));
            }
        }

        for (let day of days)
        {
            day.availableAppointments = [];
            day.occupiedAppointments = [];

            for (let time of availableTime)
            {
                let timeEnd: Time = new Time(time.rangeStart.Hours, time.rangeStart.Minutes + time.defaultInterval + time.setuptime, time.rangeStart.Seconds);
                let start: moment.Moment = moment(day.date).add(time.rangeStart.Hours, "hours").add(time.rangeStart.Minutes, "minutes").add(time.rangeStart.Seconds, "seconds");
                let end: moment.Moment = moment(day.date).add(timeEnd.Hours, "hours").add(timeEnd.Minutes, "minutes").add(timeEnd.Seconds, "seconds");

                day.availableAppointments.push({
                    calendarId: time.calendarId,
                    starttime: moment(start),
                    endtime: moment(end),
                    setuptime: time.setuptime,
                    timeGroups: time.calendarIds
                });
            }

            day.availableAppointments.removeAt(block =>
            {
                for (let appointment of day.appointments.where(e => e.calendar_id === block.calendarId))
                {
                    let start: moment.Moment = moment(appointment.starttime).subtract((appointment.setuptime_after > 0 ? appointment.setuptime_before : 0), "minutes");
                    let end: moment.Moment = moment(appointment.endtime).add((appointment.setuptime_after > 0 ? appointment.setuptime_after : 0), "minutes");
                    let repeatStart: moment.Moment;
                    let repeatEnd: moment.Moment;

                    if (appointment.repeat_until)
                    {
                        repeatStart = moment(block.starttime.format("L") + " " + start.format("hh:mm:ss"), "DD.MM.YYYY hh:mm:ss");
                        repeatEnd = moment(block.starttime.format("L") + " " + end.format("hh:mm:ss"), "DD.MM.YYYY hh:mm:ss");
                    }
                    
                    if ((block.starttime < end && start < block.endtime) || (appointment.repeat_until && (block.starttime < repeatEnd && repeatStart < block.endtime)))
                    {
                        if (appointment.appointment_type !== "block")
                        {
                            let userId: number = appointment.user_id;
                            let mortician: modelsGet.IPerson = utilsCalendar.getMortician(appointment);
                            if (mortician != null)
                            {
                                let user: modelsGet.IUser = users.where(u => u.mortician_id === mortician.id).first();
                                if (user != null)
                                {
                                    userId = user.id;
                                }
                            }

                            day.occupiedAppointments.push({
                                calendarId: block.calendarId,
                                userId: userId,
                                starttime: moment(block.starttime),
                                endtime: moment(block.endtime),
                                timeGroups: block.timeGroups
                            });
                        }

                        return true;
                    }
                }

                if (block.timeGroups != null && block.timeGroups.length > 0)
                {
                    for (let appointment of day.appointments.where(app => {
                        console.log(app.calendar_id, block.timeGroups.contains(app.calendar_id));
                        return block.timeGroups.contains(app.calendar_id);
                    }))
                    {
                        if ((block.starttime < moment(appointment.endtime)) && (moment(appointment.starttime) < block.endtime))
                        {
                            day.occupiedAppointments.push({
                                calendarId: block.calendarId,
                                starttime: moment(block.starttime),
                                endtime: moment(block.endtime),
                                timeGroups: block.timeGroups
                            });
                            return true;
                        }
                    }
                }

                let settings: modelsGet.ICalendarSettings = JSON.parse(calendars.where(c => c.id === block.calendarId).first().settings);
                if (settings.time.locks != null && settings.time.locks.any(l => l.weekday_de.toLowerCase() === moment.weekdays(moment(block.starttime).day()).toLowerCase()))
                {
                    for (let item of settings.time.locks.where(l => l.weekday_de.toLowerCase() === moment.weekdays(moment(block.starttime).day()).toLowerCase()))
                    {
                        let blockFrom: Time = Time.parse(block.starttime.format("HH:mm:ss"));
                        let blockTo: Time = Time.parse(block.endtime.format("HH:mm:ss"));
                        let lockFrom: Time = Time.parse(item.from);
                        let lockTo: Time = Time.parse(item.to);

                        if (blockFrom.lessThan(lockTo) && lockFrom.lessThan(blockTo))
                        {
                            return true;
                        }
                    }
                }

                // Daten werden für die Lückenlose Terminvergabe benötigt.
                if (settings.time.complete_appointment)
                {
                    day.occupiedAppointments.push({
                        calendarId: block.calendarId,
                        starttime: moment(block.starttime),
                        endtime: moment(block.endtime),
                        isForCompleteAppointment: true,
                        timeGroups: block.timeGroups
                    });
                }

                return false;
            });

            // Lückenlose Terminvergabe.
            day.occupiedAppointments.removeAt(block =>
            {
                if (block.isForCompleteAppointment)
                {
                    let appointments = day.appointments.where(a => a.calendar_id === block.calendarId || block.timeGroups.contains(a.calendar_id));
                    if (appointments != null && appointments.length > 0)
                    {
                        for (let appointment of appointments)
                        {
                            let start: moment.Moment = moment(appointment.starttime).subtract(appointment.setuptime_before > 0 ? appointment.setuptime_before : 0, "minutes");
                            let end: moment.Moment = moment(appointment.endtime).add(appointment.setuptime_after > 0 ? appointment.setuptime_after : 0, "minutes");

                            if (end.isSame(block.starttime))
                            {
                                return true;
                            }
                            else if (start.isSame(block.endtime))
                            {
                                return true;
                            }
                        }
                    }
                    else
                    {
                        return true;
                    }
                }
            });

            day.availableAppointments.removeAt(block =>
            {
                for (let lock of day.occupiedAppointments.where(a => a.isForCompleteAppointment))
                {
                    if (block.calendarId === lock.calendarId && block.starttime.isSame(lock.starttime) && block.endtime.isSame(lock.endtime))
                        return true;
                }
            });
        }
    }
    
    /**
     * Generiert den Link für die Kalenderansicht auswahl.
     * @param viewMode Die Kalenderansicht aus der der Link generiert werden soll.
     */
    private getViewModeLink = (viewMode: enums.CalendarViewMode): string =>
    {
        var { route, selectedDate } = this.props;
        var { settings } = this.state;
        var currentRoute: uri.URI = route();
        var currentDate: moment.Moment = moment(selectedDate);

        
        if (viewMode === enums.CalendarViewMode.month)
        {
            return currentRoute.setNewLocation({
                date: selectedDate,
                viewMode: viewMode
            }).hrefRelative();
        }
        else if (viewMode === enums.CalendarViewMode.workWeek)
        {
            let visibleDays: number[] = utilsCalendar.getVisibleDays(settings.hiddenDaysInWorkWeek);
            let startOfWeek: moment.Moment = currentDate.getStartOfWeek(visibleDays.first());
            
            return currentRoute.setNewLocation({
                date: startOfWeek,
                viewMode: viewMode
            }).hrefRelative();
        }
        else if (viewMode === enums.CalendarViewMode.week || viewMode === enums.CalendarViewMode.day)
        {
            return currentRoute.setNewLocation({
                date: currentDate,
                viewMode: viewMode
            }).hrefRelative();
        }
        else
        {
            return currentRoute.setNewLocation({
                date: currentDate,
                viewMode: viewMode,
                replaceSearch: true
            }).hrefRelative();
        }
    }

    /**
     * Klappt die SideBar ein oder auf.
     */
    private onSideBarVisibilityChanged = (): void =>
    {
        const isOpen = this.state.isSideBarOpen;
        this.setState({
            isSideBarOpen: !isOpen
        }, () => utilsStorage.saveCalendarTempData({ isSideBarOpen: !isOpen }));
    }

    /**
     * Wird ausgeführt bevor sich das Popup für die Termin Details öffnet.
     * @param e Das auslösende Ereignis.
     * @param item Der gebundene Termin.
     */
    private onAppointmentDetailsPopupOpening = (e: MouseEvent, item: modelsGet.IAppointment): boolean =>
    {
        utilsCalendar.getEventLinksCountAsync(item.id, item.parent_event_id).then((count) =>
        {
            utilsCalendar.getLastTimeOfChangeAsync(item).then((lastChange) =>
            {
                this.setState({
                    popupAppointment: item,
                    popupAppointmentLinkCount: count,
                    popupAppointmentLastChange: lastChange
                });
            });
        });

        return true;
    }

    /**
     * Wird ausgeführt bevor sich das Popup für die Termin Details öffnet.
     * @param e Das auslösende Ereignis.
     * @param item Der gebundene Termin.
     */
    private onBlockDetailsPopupOpening = (e: MouseEvent, item: any): boolean =>
    {
        var appointment: modelsGet.IAppointment = item.appointment || item;

        if (appointment.appointment_type !== "block")
        {
            utilsCalendar.getEventLinksCountAsync(appointment.id, appointment.parent_event_id).then((count) =>
            {
                utilsCalendar.getLastTimeOfChangeAsync(appointment).then((lastChange) =>
                {
                    this.setState({
                        popupAppointment: appointment,
                        popupAppointmentLinkCount: count,
                        popupAppointmentLastChange: lastChange,
                        popupBlock: null
                    });
                });
            });
        }
        else
        {
            this.setState({
                popupAppointment: null,
                popupAppointmentLinkCount: 0,
                popupAppointmentLastChange: null,
                popupBlock: appointment
            });
        }

        return true;
    }

    /**
     * Wird ausgeführt sobald sich das Datum im Datepicker ändert.
     * @param newDate Das Datum zu dem gewechselt werden soll.
     * @param type Gibt an was im Datepicker geändert wurde.
     * @param history React-Router history.
     */
    private onDatepickerDateChanged = (newDate: moment.Moment, type: "year" | "month" | "day", history: any): void =>
    {
        var { route, selectedViewMode } = this.props;
        var currentRoute = route();

        switch (type)
        {
            case "year":
                {
                    history.push(currentRoute.setNewLocation({
                        date: moment(newDate).startOf("month")
                    }).hrefRelative());
                }
                break;
            case "month":
                {
                    history.push(currentRoute.setNewLocation({
                        date: moment(newDate).startOf("month")
                    }).hrefRelative());
                }
                break;
            case "day":
                {
                    history.push(currentRoute.setNewLocation({
                        date: moment(newDate)
                    }).hrefRelative());
                }
                break;
        }
    }

    /**
     * Wird ausgeführt, sobald die ToolBarItems previous, today oder next geklickt werden.
     * @param history React-Router history.
     * @param type Der des Items.
     */
    private onToolBarNavigationClick = (history: any, type: "today" | "previous" | "next"): void =>
    {
        var { route, selectedDate, selectedViewMode } = this.props;
        var { settings } = this.state;
        var currentRoute: uri.URI = route();
        var date: moment.Moment = (type === "today") ? moment() : moment(selectedDate);

        switch (selectedViewMode)
        {
            case enums.CalendarViewMode.month:
                {
                    if (type === "previous")
                    {
                        date = date.subtract(1, "months");
                    }
                    else if (type === "next")
                    {
                        date = date.add(1, "months");
                    }
                }
                break;
            case enums.CalendarViewMode.week:
            case enums.CalendarViewMode.workWeek:
                {
                    let visibleDays = utilsCalendar.getVisibleDays(settings.hiddenDaysInWorkWeek);
                    let firstDay = visibleDays.first();
                    date = moment(date).getStartOfWeek(firstDay);

                    if (type === "previous")
                    {
                        date = date.subtract(1, "weeks");
                    }
                    else if (type === "next")
                    {
                        date = date.add(1, "weeks");
                    }
                }
                break;
            case enums.CalendarViewMode.day:
            case enums.CalendarViewMode.planning:
                {
                    if (type === "previous")
                    {
                        date = date.subtract(1, "days");
                    }
                    else if (type === "next")
                    {
                        date = date.add(1, "days");
                    }
                }
                break;
        }

        currentRoute.segment(2, date.year().toString());
        currentRoute.segment(3, (date.isoMonth()).toString());

        if (selectedViewMode !== enums.CalendarViewMode.month)
            currentRoute.segment(4, date.date().toString());

        history.push(currentRoute.hrefRelative());
    }

    /**
     * Wird ausgeführt, sobald ein Termin kopiert wird.
     * @param appointment Der Termin der kopiert werden soll.
     */
    private onAppointmentCopyMenuItemClick = (appointment: modelsGet.IAppointment): void =>
    {
        this._copiedAppointmentId = appointment.id;
        this._subjectTypeId = appointment.subject_type_id;
    }

    /**
     * Rendert die Termin Details für das Popup.
     */
    private renderPopupAppointmentDetails(): JSX.Element
    {
        var { popupAppointment, popupAppointmentLinkCount, popupAppointmentLastChange } = this.state

        // Damit die Details angezeigt werden können, muss ein Termin vorhanden sein.
        if (popupAppointment == null)
            return null;
        
        var eventColor: string = utilsCalendar.getEventColor(popupAppointment);
        var isUserAuthorizedToSeeAppointment: boolean = popupAppointment.appointment_type !== "not_authorized";

        // Betreff Zeile generieren.
        var subject: JSX.Element;
        if (isUserAuthorizedToSeeAppointment)
        {
            var linkCount: JSX.Element = null;
            if (popupAppointmentLinkCount > 0)
            {
                linkCount = (
                    <div className="po-event-details_content_subject_links">
                        <i className="mdi mdi-link-variant"></i>
                        <span>{popupAppointmentLinkCount}</span>
                    </div>
                );
            }

            subject = (
                <div className="po-event-details_content_subject">
                    <span className="po-event-details_content_subject_text">
                        {popupAppointment.subject + ((popupAppointment.parent_event_id > 0) ? "(Folgetermin)" : "")}
                    </span>
                    {linkCount}
                </div>
            );
        }
        else
        {
            subject = (
                <div className="po-event-details_content_subject">
                    <span className="po-event-details_content_subject_text">Belegt</span>
                </div>
            );
        }

        // Detailansicht des Termin generieren.
        var details: JSX.Element = null;
        if (isUserAuthorizedToSeeAppointment)
        {
            let deceasedPerson: modelsGet.IPerson = utilsCalendar.getDeceasedPerson(popupAppointment);
            let mortician: modelsGet.IPerson = utilsCalendar.getMortician(popupAppointment);

            details = (
                <table className="po-event-details_content_fields">
                    <tbody>
                        {
                            deceasedPerson != null && (
                                <tr>
                                    <td className="po-event-details_content_fields-col">
                                        <span className="po-event-details_content_field-name">Verstorbener:</span>
                                    </td>
                                    <td>
                                        <span className="po-event-details_content_field-value">
                                            {deceasedPerson.fullname_and_address_without_title}
                                        </span>
                                    </td>
                                </tr>
                            )
                        }
                        {
                            mortician != null && (
                                <tr>
                                    <td className="po-event-details_content_fields-col">
                                        <span className="po-event-details_content_field-name">Bestatter:</span>
                                    </td>
                                    <td>
                                        <span className="po-event-details_content_field-value">
                                            {mortician.fullname_and_address_without_title}
                                        </span>
                                    </td>
                                </tr>
                            )
                        }
                        {
                            popupAppointment.calendar_name != null && (
                                <tr>
                                    <td className="po-event-details_content_fields-col">
                                        <span className="po-event-details_content_field-name">Kalender:</span>
                                    </td>
                                    <td>
                                        <span className="po-event-details_content_field-value">
                                            {popupAppointment.calendar_name}
                                        </span>
                                    </td>
                                </tr>
                            )
                        }
                        {
                            popupAppointment.status != null && (
                                <tr>
                                    <td className="po-event-details_content_fields-col">
                                        <span className="po-event-details_content_field-name">Status:</span>
                                    </td>
                                    <td>
                                        <span className="po-event-details_content_field-value">
                                            {popupAppointment.status}
                                        </span>
                                    </td>
                                </tr>
                            )
                        }
                        {
                            popupAppointment.display_type_text != null && (
                                <tr>
                                    <td className="po-event-details_content_fields-col">
                                        <span className="po-event-details_content_field-name">Anzeigetyp:</span>
                                    </td>
                                    <td>
                                        <span className="po-event-details_content_field-value">
                                            {popupAppointment.display_type_text}
                                        </span>
                                    </td>
                                </tr>
                            )
                        }
                        {
                            (popupAppointment.setuptime_before != null || popupAppointment.setuptime_after) && (
                                <tr>
                                    <td className="po-event-details_content_fields-col">
                                        <span className="po-event-details_content_field-name">Zeit + Rüstzeit:</span>
                                    </td>
                                    <td>
                                        <span className="po-event-details_content_field-value">
                                            {utilsCalendar.getTimeWithSetuptimeText(popupAppointment)}
                                        </span>
                                    </td>
                                </tr>
                            )
                        }
                        {
                            popupAppointment.burial_method != null && (
                                <tr>
                                    <td className="po-event-details_content_fields-col">
                                        <span className="po-event-details_content_field-name">Bestattungsart:</span>
                                    </td>
                                    <td>
                                        <span className="po-event-details_content_field-value">
                                            {popupAppointment.burial_method}
                                        </span>
                                    </td>
                                </tr>
                            )
                        }
                        {
                            popupAppointment.grave_no != null && (
                                <tr>
                                    <td className="po-event-details_content_fields-col">
                                        <span className="po-event-details_content_field-name">Grabnr.:</span>
                                    </td>
                                    <td>
                                        <span className="po-event-details_content_field-value">
                                            {popupAppointment.grave_no}
                                        </span>
                                    </td>
                                </tr>
                            )
                        }
                        {
                            popupAppointment.remark != null && (
                                <tr>
                                    <td className="po-event-details_content_fields-col">
                                        <span className="po-event-details_content_field-name">Bemerkung:</span>
                                    </td>
                                    <td>
                                        <span className="po-event-details_content_field-value">
                                            {popupAppointment.remark}
                                        </span>
                                    </td>
                                </tr>
                            )
                        }
                        {
                            popupAppointment.mourning_hall_name != null && (
                                <tr>
                                    <td className="po-event-details_content_fields-col">
                                        <span className="po-event-details_content_field-name">Trauerhalle:</span>
                                    </td>
                                    <td>
                                        <span className="po-event-details_content_field-value">
                                            {popupAppointment.mourning_hall_name}
                                        </span>
                                    </td>
                                </tr>
                            )
                        }
                    </tbody>
                </table>
            );
        }

        // Labels generieren.
        var labels: JSX.Element = null;
        if (isUserAuthorizedToSeeAppointment)
        {
            let lbs: modelsGet.ILabel[] = utilsCalendar.getLabels(popupAppointment);
            if (lbs != null && lbs.length > 0)
            {
                labels = (
                    <div className="po-event-details_labels">
                        {
                            lbs.map((l) =>
                            {
                                return (
                                    <div className={`label label--${l.label_color}`}>
                                        <span className="label_text">{l.label}</span>
                                    </div>
                                );
                            })
                        }
                    </div>
                );
            }
        }

        // Letzte Änderung generieren.
        var lastChange: JSX.Element = null;
        if (isUserAuthorizedToSeeAppointment && popupAppointmentLastChange != null)
        {
            lastChange = (
                <div className="po-event-details_edited">
                    <img
                        className="po-event-details_edited_user_img"
                        src={popupAppointmentLastChange.user.avatar} />
                    <span className="po-event-details_edited_user_name">
                        {popupAppointmentLastChange.user.fullname}
                    </span>
                    <span className="po-event-details_edited_at">
                        {popupAppointmentLastChange.change_date.format("L")}
                    </span>
                </div>
            );
        }

        // Zeitraum ermitteln.
        var timespan: string;
        if (isUserAuthorizedToSeeAppointment)
            timespan = moment(popupAppointment.starttime).format("dd L HH:mm") + ' - ' + moment(popupAppointment.endtime).format("HH:mm");
        else
            timespan = moment(popupAppointment.starttime).subtract(popupAppointment.setuptime_before > 0 ? popupAppointment.setuptime_before : 0, "minutes").format("dd L HH:mm") + ' - ' + moment(popupAppointment.endtime).add(popupAppointment.setuptime_after > 0 ? popupAppointment.setuptime_after : 0, "minutes").format("HH:mm");

        if (popupAppointment != null)
        {
            return (
                <div className="po-event-details">
                    <div
                        className="po-event-details_date"
                        style={{
                            backgroundColor: utilsColor.lighten(eventColor, 0.7),
                            color: utilsColor.darken(eventColor, 0.3)
                        }}>
                        {timespan}
                    </div>
                    <div className="po-event-details_content">
                        {subject}
                        {details}
                    </div>
                    {labels}
                    {lastChange}
                </div>
            );
        }
    }

    /**
     * Rendert die Details einer Sperre für das Popup.
     */
    private renderPopupBlockDetails(): JSX.Element
    {
        var { popupBlock } = this.state

        if (popupBlock == null)
            return null;

        return (
            <div className="po-event-details">
                <div
                    className="po-event-details_date"
                    style={{
                        backgroundColor: utilsColor.lighten("#df4019", 0.7),
                        color: utilsColor.darken("#df4019", 0.3)
                    }}>
                    {moment(popupBlock.starttime).format('dd L HH:mm') + ' - ' + moment(popupBlock.endtime).format('HH:mm')}
                </div>
                <div className="po-event-details_content">
                    <div className="po-event-details_content_subject">
                        <span className="po-event-details_content_subject_text">Gesperrt</span>
                    </div>
                    <table className="po-event-details_content_fields">
                        <tbody>
                            {
                                popupBlock.description != null && (
                                    <tr>
                                        <td className="po-event-details_content_fields-col">
                                            <span className="po-event-details_content_field-name">Grund:</span>
                                        </td>
                                        <td>
                                            <span className="po-event-details_content_field-value">
                                                {popupBlock.description}
                                            </span>
                                        </td>
                                    </tr>
                                )
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    /**
     * Rendert das Kontextmenü eines Termins.
     * @param data Enthält das Event oder Lock Objekt auf das geklickt wurde.
     */
    private renderAppointmentContextMenu(dataSource: any): JSX.Element
    {
        var isSideBar: number = dataSource.isSideBar;
        var calendar: number = dataSource.calendar;
        var appointment: modelsGet.IAppointment = dataSource.appointment;
        var isAppointment: boolean = appointment.appointment_type === "appointment";
        var uri: uri.URI = this.props.route();

        // Links generieren.
        var gotoEventOrLock: string;
        var eventOrLockLink = {
            pathname: "",
            state: {
                modal: true
            }
        };

        if (isAppointment)
        {
            eventOrLockLink.pathname = `/event/${appointment.id}${calendar > 0 ? "?calendar=" + calendar : ""}`;

            if (this.state.selectedObject.type === enums.CalendarObjectType.calendar)
            {
                gotoEventOrLock = uri.setNewLocation({
                    selectedObjectId: {
                        type: enums.CalendarObjectType.calendar,
                        id: appointment.calendar_id
                    },
                    date: moment(appointment.starttime)
                }).hrefRelative();
            }
            else
            {
                gotoEventOrLock = uri.setNewLocation({
                    selectedObjectId: {
                        type: enums.CalendarObjectType.calendarGroup,
                        id: appointment.calendar_group_id
                    },
                    date: moment(appointment.starttime)
                }).hrefRelative();
            }
        }
        else
        {
            eventOrLockLink.pathname = `/lock/${appointment.id}${appointment.repeat_until ? "?series=true" : ""}`;
            gotoEventOrLock = uri.setNewLocation({
                selectedObject: this.state.selectedObject,
                date: moment(appointment.starttime)
            }).hrefRelative();
        }

        return (
            <ContextMenu>
                <MenuItem
                    icon="arrow-top-right"
                    header="Öffnen..."
                    linkTo={eventOrLockLink} />

                {isSideBar && (
                    <MenuItem
                        icon="calendar"
                        header="Im Kalender anzeigen"
                        linkTo={gotoEventOrLock} />
                )}

                {!isSideBar && isAppointment && <Separator />}
                {!isSideBar && isAppointment && (
                    <MenuItem
                        dataSource={appointment}
                        icon="content-copy"
                        header="Kopieren"
                        onClick={this.onAppointmentCopyMenuItemClick} />
                )}
            </ContextMenu>
        );
    }

    /**
     * Render das Kontextmenü für das erstellen von Terminen, kopieren von Terminen usw. für die Monatsansicht.
     * @param date Das geklickte Datum.
     */
    private renderMonthContextMenu(date: moment.Moment): JSX.Element
    {
        const { user } = this.state;
        var newEventLink: string = "/event/new?date=" + date.toISOString();
        var newLockLink: string = "/lock/new?date=" + date.toISOString();
        var newLockSeriesLink: string = "/lock/new?date=" + date.toISOString() + "&series=true";
        var copyEventLink: string = "/event/new?date=" + date.toISOString() + "&copy=" + this._copiedAppointmentId;
        var followUpEventLink: string = "/event/new?date=" + date.toISOString() + "&followUp=" + this._copiedAppointmentId;

        return (
            <ContextMenu>
                {
                    user.claims.can_create_event &&
                    <MenuItem
                        icon="plus"
                        iconColor="#107c10"
                        header="Neuer Termin"
                        linkTo={{
                            pathname: newEventLink,
                            state: {
                                modal: true
                            }
                        }} />
                }
                {
                    user.claims.can_create_lock &&
                    <MenuItem
                        icon="block-helper"
                        header="Neue Sperre"
                        linkTo={{
                            pathname: newLockLink,
                            state: {
                                modal: true
                            }
                        }} />
                }
                {
                    user.claims.can_create_lock &&
                    <MenuItem
                        icon="block-helper"
                        header="Neue Sperrserie"
                        linkTo={{
                            pathname: newLockSeriesLink,
                            state: {
                                modal: true
                            }
                        }} />
                }
                {user.claims.can_create_event && <Separator />}
                {
                    user.claims.can_create_event &&
                    <MenuItem
                        dataSource={date}
                        icon="content-paste"
                        header="Einfügen"
                        linkTo={{
                            pathname: copyEventLink,
                            state: {
                                modal: true
                            }
                        }}
                        isDisabled={this._copiedAppointmentId === 0} />
                }
                {
                    user.claims.can_create_event &&
                    <MenuItem
                        dataSource={date}
                        icon="calendar-plus"
                        header="Folgetermin erstellen"
                        linkTo={{
                            pathname: followUpEventLink,
                            state: {
                                modal: true
                            }
                        }}
                        isDisabled={this._copiedAppointmentId === 0 || (this._subjectTypeId === null || this._subjectTypeId !== 1)} />
                }
            </ContextMenu>
        );
    }

    /**
     * Render das Kontextmenü für das erstellen von Terminen, kopieren von Terminen usw. für alle anderen Ansichten.
     * @param date Das geklickte Datum.
     */
    private renderDayContextMenu({ date, enddate, calendar }): JSX.Element
    {
        const { user } = this.state;
        const { selectedObject, selectedCalendar } = this.state;

        if (calendar == null)
        {
            if (selectedObject.type === enums.CalendarObjectType.calendarGroup)
                calendar = selectedCalendar;
            else
                calendar = selectedObject.data;
        }

        var setuptimeAfter: number = 0;

        if (enddate != null)
        {
            var settings: modelsGet.ICalendarSettings = JSON.parse(calendar.settings);
            if (settings.time.automatic_setuptime && settings.time.setuptime > 0)
            {
                enddate = moment(enddate).subtract(settings.time.setuptime, "minutes");
                setuptimeAfter = settings.time.setuptime;
            }
        }

        // New Event Link
        var newEventLink: string[] = ["/event/new?date=" + date.toISOString()];
        if (enddate != null)
        {
            newEventLink.push("enddate=" + enddate.toISOString());
            newEventLink.push((setuptimeAfter > 0 ? "setuptime=" + setuptimeAfter.toString() : ""));
        }
        newEventLink.push((calendar != null ? "calendar=" + calendar.id : ""));

        // New Lock Link
        var newLockLink: string[] = ["/lock/new?date=" + date.toISOString()];
        if (enddate != null)
        {
            if (setuptimeAfter > 0)
                newLockLink.push("enddate=" + moment(enddate).add(setuptimeAfter, "minutes").toISOString());
            else
                newLockLink.push("enddate=" + enddate.toISOString());
        }
        newLockLink.push((calendar != null ? "calendar=" + calendar.id : ""));

        // New Lock Series Link
        var newLockSeriesLink: string[] = [...newLockLink];
        newLockSeriesLink.push("series=true");

        // Copy Event Link
        var copyEventLink: string[] = ["/event/new?date=" + date.toISOString()];
        if (enddate != null)
        {
            copyEventLink.push("enddate=" + enddate.toISOString());
            copyEventLink.push((setuptimeAfter > 0 ? "setuptime=" + setuptimeAfter.toString() : ""));
        }
        copyEventLink.push((calendar != null ? "calendar=" + calendar.id : ""));
        copyEventLink.push("copy=" + this._copiedAppointmentId);

        // Follow Up Event Link
        var followUpEventLink: string[] = ["/event/new?date=" + date.toISOString()];
        if (enddate != null)
        {
            followUpEventLink.push("enddate=" + enddate.toISOString());
            followUpEventLink.push((setuptimeAfter > 0 ? "setuptime=" + setuptimeAfter.toString() : ""));
        }
        followUpEventLink.push("followUp=" + this._copiedAppointmentId);
        followUpEventLink.push((calendar != null ? "calendar=" + calendar.id : ""));

        return (
            <ContextMenu>
                <MenuItem
                    icon="plus"
                    iconColor="#107c10"
                    header="Neuer Termin"
                    linkTo={{
                        pathname: newEventLink.join("&"),
                        state: {
                            modal: true
                        }
                    }} />
                {
                    user.claims.can_create_lock &&
                    <MenuItem
                        icon="block-helper"
                        header="Neue Sperre"
                        linkTo={{
                            pathname: newLockLink.join("&"),
                            state: {
                                modal: true
                            }
                        }} />
                }
                {
                    user.claims.can_create_lock &&
                    <MenuItem
                        icon="block-helper"
                        header="Neue Sperrserie"
                        linkTo={{
                            pathname: newLockSeriesLink.join("&"),
                            state: {
                                modal: true
                            }
                        }} />
                }
                <Separator />
                <MenuItem
                    dataSource={date}
                    icon="content-paste"
                    header="Einfügen..."
                    linkTo={{
                        pathname: copyEventLink.join("&"),
                        state: {
                            modal: true
                        }
                    }}
                    isDisabled={this._copiedAppointmentId === 0} />
                <MenuItem
                    dataSource={date}
                    icon="calendar-plus"
                    header="Folgetermin erstellen..."
                    linkTo={{
                        pathname: followUpEventLink.join("&"),
                        state: {
                            modal: true
                        }
                    }}
                    isDisabled={this._copiedAppointmentId === 0 || (this._subjectTypeId === null || this._subjectTypeId !== 1)} />
            </ContextMenu>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            refresh,
            route,
            selectedDate,
            selectedViewMode
        } = this.props;

        const currentRoute: uri.URI = route();
        const today: moment.Moment = moment();

        const {
            isLoading,
            user,
            selectedObject,
            selectedCalendar,
            hours,
            weeks,
            days,
            appointments,
            settings,
            visibleDatepickerCount,
            isSideBarOpen,
            timeGroups
        } = this.state;

        const Dp = () => (
            <Route render={({ history }) => (
                <Datepicker
                    selectedDate={selectedDate}
                    selectedViewMode={selectedViewMode}
                    count={visibleDatepickerCount}
                    isSelectedRangeVisible
                    onDateChanged={(newDate, type) => this.onDatepickerDateChanged(newDate, type, history)} />
            )} />
        );

        const PreviousItem = () => (
            <Route render={({ history }) => (
                <ToolBarItem
                    icon="chevron-left"
                    onClick={() => this.onToolBarNavigationClick(history, "previous")} />
            )} />
        );

        const TodayItem = () => (
            <Route render={({ history }) => (
                <ToolBarItem
                    text="Heute"
                    onClick={() => this.onToolBarNavigationClick(history, "today")} />
            )} />
        );

        const NextItem = () => (
            <Route render={({ history }) => (
                <ToolBarItem
                    icon="chevron-right"
                    onClick={() => this.onToolBarNavigationClick(history, "next")} />
            )} />
        );

        var calendarView = null;

        if (isLoading)
        {
            calendarView = (
                <div className="calendar-loader">
                    <Loader loaderStyle={LoaderStyle.style1} />
                </div>
            );
        }
        else
        {
            switch (selectedViewMode)
            {
                case enums.CalendarViewMode.day:
                case enums.CalendarViewMode.workWeek:
                case enums.CalendarViewMode.week:
                    {
                        calendarView = (
                            <CalendarDay
                                currentUser={user}
                                settings={{
                                    eventDayViewTemplate: settings.eventDayViewTemplate,
                                    showLabelsAsInitials: settings.showLabelsAsInitials
                                }}
                                selectedObject={selectedObject}
                                selectedDate={selectedDate}
                                selectedCalendar={selectedCalendar}
                                selectedViewMode={selectedViewMode}
                                hours={hours}
                                days={days}
                                onAppointmentPopupOpening={this.onBlockDetailsPopupOpening} />
                        );
                    }
                    break;
                case enums.CalendarViewMode.month:
                    {
                        calendarView = (
                            <CalendarMonth
                                route={route}
                                currentUser={user}
                                settings={{
                                    eventDayViewTemplate: settings.eventDayViewTemplate
                                }}
                                selectedObject={selectedObject}
                                selectedDate={selectedDate}
                                selectedCalendar={selectedCalendar}
                                weeks={weeks}
                                appointments={appointments}
                                onAppointmentPopupOpening={this.onBlockDetailsPopupOpening} />
                        );
                    }
                    break;
                case enums.CalendarViewMode.planning:
                    {
                        calendarView = (
                            <CalendarPlanning
                                currentUser={user}
                                settings={{
                                    eventDayViewTemplate: settings.eventDayViewTemplate,
                                    showLabelsAsInitials: settings.showLabelsAsInitials
                                }}
                                selectedObject={selectedObject}
                                selectedDate={selectedDate}
                                selectedCalendar={selectedCalendar}
                                selectedViewMode={selectedViewMode}
                                hours={hours}
                                days={days}
                                timeGroups={timeGroups}
                                onAppointmentPopupOpening={this.onBlockDetailsPopupOpening} />
                        );
                    }
                    break;
            }
        }

        if (selectedObject != null)
        {
            //let calendarColor: string = "";
            //if (selectedCalendar != null)
            //    calendarColor = selectedCalendar.color;
            //else
            //    calendarColor = selectedObject.data.color;

            // Die ViewModes ermitteln die der Benutzer sehen darf.
            var viewModes: number[] = [];
            if (user.claims.forbidden_view_modes != null && user.claims.forbidden_view_modes.length > 0)
                viewModes = user.claims.forbidden_view_modes;

            return (
                <div className="calendar-container">
                    <SideBar
                        refresh={refresh}
                        isOpen={isSideBarOpen}
                        selectedDate={selectedDate}
                        selectedViewMode={selectedViewMode}
                        width={251}
                        onDateChanged={this.onDatepickerDateChanged}
                        onExpanded={this.onSideBarVisibilityChanged}
                        onCollapsed={this.onSideBarVisibilityChanged}
                        onEventDetailsPopupOpening={this.onAppointmentDetailsPopupOpening} />
                    <ToolBar
                        style={utils.getGridChildStyle(2, 2, 1, 1)}
                        hasTopBorder={false}>
                        <PreviousItem />
                        <TodayItem />
                        <NextItem />
                        <ToolBarSeparator />
                        <PopupProvider
                            id="datepicker-popup"
                            trigger={PopupTrigger.Click}
                            refPropName="elementRef">
                            <ToolBarItem text={utilsCalendar.getFormatedDateLabel(selectedDate, selectedViewMode, settings.hiddenDaysInWorkWeek)} />
                        </PopupProvider>
                        <ToolBarSeparator />
                        <PopupProvider
                            id="viewmode-popup"
                            trigger={PopupTrigger.Click}
                            refPropName="elementRef">
                            <ToolBarItem
                                icon="image-filter"
                                text={utilsViewMode.getViewModeReadableString(selectedViewMode)} />
                        </PopupProvider>
                    </ToolBar>
                    {calendarView}
                    <Popup
                        id="event-info"
                        align={PopupAlign.targetNodeLeftTop}>
                        {this.renderPopupAppointmentDetails()}
                        {this.renderPopupBlockDetails()}
                    </Popup>
                    <Popup id="event-contextmenu">
                        {(dataSource) => this.renderAppointmentContextMenu(dataSource)}
                    </Popup>
                    <Popup id="day-contextmenu">
                        {
                            (dataSource) =>
                            {
                                if (selectedViewMode === enums.CalendarViewMode.month)
                                    return this.renderMonthContextMenu(dataSource);
                                else
                                    return this.renderDayContextMenu(dataSource)
                            }
                        }
                    </Popup>
                    <Popup
                        id="datepicker-popup"
                        align={PopupAlign.targetNodeBottomLeft}>
                        <Dp />
                    </Popup>
                    <Popup
                        id="viewmode-popup"
                        align={PopupAlign.targetNodeBottomLeft}>
                        <ContextMenu>
                            {
                                !viewModes.contains(enums.CalendarViewMode.day) &&
                                <MenuItem
                                    header="Tag"
                                    linkTo={this.getViewModeLink(enums.CalendarViewMode.day)} />
                            }
                            {
                                !viewModes.contains(enums.CalendarViewMode.workWeek) &&
                                <MenuItem
                                    header="Arbeitswoche"
                                    linkTo={this.getViewModeLink(enums.CalendarViewMode.workWeek)} />
                            }
                            {
                                !viewModes.contains(enums.CalendarViewMode.week) &&
                                <MenuItem
                                    header="Woche"
                                    linkTo={this.getViewModeLink(enums.CalendarViewMode.week)} />
                            }
                            {
                                !viewModes.contains(enums.CalendarViewMode.month) &&
                                <MenuItem
                                    header="Monat"
                                    linkTo={this.getViewModeLink(enums.CalendarViewMode.month)} />
                            }
                            {
                                !viewModes.contains(enums.CalendarViewMode.planning) &&
                                <MenuItem
                                    header="Planungsansicht"
                                    linkTo={this.getViewModeLink(enums.CalendarViewMode.planning)}
                                    isDisabled={selectedObject.type === enums.CalendarObjectType.calendar} />
                            }
                        </ContextMenu>
                    </Popup>
                </div>
            );
        }
        else
        {
            return null;
        }
    }
}